import React, { useMemo } from 'react';
import {
  Box,
  Card,
  LineChart,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { InfoOutlined } from '@barracuda-internal/bds-core/dist/Icons/Core';

/**
 * "Trend strip" pattern from the reference. A single thin sparkline on the
 * left and a compact top-N list on the right, all inside one card.
 *
 * Props:
 *   title:  card heading
 *   data:   array of numbers (oldest -> newest) — the chart series
 *   total:  optional summary number shown next to the title (e.g. "33")
 *   color:  borderColor token. Defaults to chart series 2 (purple).
 *   fill:   backgroundColor token (alpha-pre-set).
 *   items:  array of { key, value } — top-N list rendered on the right
 *   itemsTitle: optional header for the list (e.g. "IPs", "Accounts")
 *   onItemClick: optional row click handler
 *   area:   true (default) = filled area; false = pure line for a
 *           lighter "trend tracking" feel. Mix area + line strips in
 *           the same dashboard to give the eye visual rhythm.
 */
const TrendStrip = ({
  title,
  data,
  total,
  color,
  fill,
  items = [],
  itemsTitle,
  onItemClick,
  area = true,
}) => {
  const tokens = useTokens();

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const strokeColor = color || tokens.chartsColorSeries2Range100;
    return {
      labels: data.map((_, i) => i),
      datasets: [
        {
          label: title,
          data,
          borderColor: strokeColor,
          // Same fix as MiniStatusChart: when fill is disabled, the
          // line still needs a non-transparent backgroundColor or
          // Chart.js / BDS renders nothing visible.
          backgroundColor: area
            ? fill || tokens.chartsColorSeries2Range20
            : strokeColor,
          fill: area,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: area ? 1.5 : 2,
        },
      ],
    };
  }, [data, title, color, fill, area, tokens]);

  const hasEntries = (data || []).some((v) => v > 0);
  const emptyState = { icon: <InfoOutlined />, text: 'No activity in this period.' };

  return (
    <Card
      bdsType="staticSubtle"
      bdsBoxShadow="default"
      bdsBorderRadius="round"
      bdsPadding="default"
    >
      <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: tokens.aliasesSpaceComponentDefault,
          }}
        >
          <Typography variant="body1Bold">{title}</Typography>
          {Number.isFinite(total) && (
            <Typography variant="h4">{total.toLocaleString()}</Typography>
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '3fr 1fr' },
            gap: tokens.aliasesSpaceContainerDefault,
            alignItems: 'stretch',
            minHeight: 180,
          }}
        >
          {/* Sparkline — fills the column height so it never gets
              vertically centered with empty space above/below. */}
          <Box
            sx={{
              width: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              {chartData && (
                <LineChart
                  chartData={chartData}
                  chartDimension={{ width: '100%', height: '100%' }}
                  hasEntries={hasEntries}
                  emptyState={emptyState}
                  hidePoints
                  maintainAspectRatio={false}
                  lineChartOptions={{
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                          title: () => '',
                          label: (ctx) =>
                            `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
                        },
                      },
                    },
                    scales: {
                      x: { display: false },
                      y: { display: false },
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Compact top-N list — tight rows so total height matches
              the chart column. */}
          <Box
            sx={{
              backgroundColor: tokens.aliasesColorBgStaticContainerNeutral,
              borderRadius: tokens.aliasesBorderRadiusDefault,
              paddingX: tokens.aliasesSpaceContainerDense,
              paddingY: tokens.aliasesSpaceComponentDense,
              minWidth: 0,
            }}
          >
            <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
              {itemsTitle && (
                <Typography
                  variant="captionSubtle"
                  sx={{ fontSize: 11, letterSpacing: 0.2 }}
                >
                  {itemsTitle}
                </Typography>
              )}
              {items.length === 0 ? (
                <Typography variant="captionSubtle">No items.</Typography>
              ) : (
                items.slice(0, 5).map((item, i) => (
                  <Stack
                    key={`${item.key}-${i}`}
                    direction="row"
                    onClick={
                      onItemClick ? () => onItemClick(item) : undefined
                    }
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: tokens.aliasesSpaceComponentDense,
                      cursor: onItemClick ? 'pointer' : 'default',
                      paddingY: '2px',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {item.key}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ flexShrink: 0, fontWeight: 600 }}
                    >
                      {typeof item.value === 'number'
                        ? item.value.toLocaleString()
                        : item.value}
                    </Typography>
                  </Stack>
                ))
              )}
            </Stack>
          </Box>
        </Box>
      </Stack>
    </Card>
  );
};

export default TrendStrip;
